import { Warning, X, Plus } from 'phosphor-react'
import { useCallback, useEffect, useState, useRef } from 'react'
import {
  Badge,
  Button,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { toast } from 'react-toastify'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import RequestModal from '../../../../components/RequestModal'
import SearchInput from '../../../../components/SearchInput'
import { useLoading } from '../../../../hooks/LoadingContext'
import DashboardLayout from '../../../../layouts/Dashboard'
import api from '../../../../services/api'
import { formatDateTSX } from '../../../../utils/tsxFormat'

import { useNavigate } from 'react-router-dom'

import styles from './styles.module.css'
import { filterDeliveryRequestsByInternalTransfers } from '../../../../utils/internalTransfers'

interface IRequest {
  id: number
  invoice: string
  request_number: string
  invoice_number: string
  receiver_name: string
  status: string
  cep: string
  city: string
  state: string
  neighborhood: string
  street: string
  street_number: string
  complement: string
  start_date: Date
  weight: number
  updated_at: string
  delivering_image_url: string
  delivering_image_key: string
  customer_rating_stars: number
  customer_rating_text: string
  request_delivery_driver_note: string
  request_delivery_time_seconds: number
  request_delivery_distance_meters: number
  vehicle_id: number
  vehicle_board: string
  vehicle_model: string
  driver_id: number
  driver_name: string
  delivery_had_problems: boolean
  created_at: Date
  note: string
  delivery_problem_solution_note: string
  is_internal_transfer: boolean
}

const EntregasProblemas: React.FC = () => {
  const navigate = useNavigate()

  const [requests, setRequests] = useState<IRequest[]>()
  const { isLoading, setIsLoading } = useLoading()
  const [hasMoreData, setHasMoreData] = useState<boolean>(true)
  const [search, setSearch] = useState('')
  const [lastSearch, setLastSearch] = useState('')
  const [filteredStatus, setFilteredStatus] = useState('')

  const [minStartDate, setMinStartDate] = useState<string | null>(null)
  const [maxStartDate, setMaxStartDate] = useState<string | null>(null)

  const [requestsChanged, setRequestsChanged] = useState<boolean>(false)
  const [showRequestModal, setShowRequestModal] = useState<boolean>(false)
  const [requestModal, setRequestModal] = useState<IRequest>()
  const [filterModal, setFilterModal] = useState(false)

  const startDateInputsChanged = useRef(false)

  const deliveryStatusMap: any = {
    allocated: 'Alocada',
    on_route: 'Em rota',
    delivered: 'Entregue',
    canceled: 'Cancelada',
  }

  const loadData = useCallback(async () => {
    const isSearchDifferent = search !== lastSearch || startDateInputsChanged.current
    if (!hasMoreData && !isSearchDifferent) return

    setIsLoading(true)

    if (isSearchDifferent) {
      setFilteredStatus('')
      setLastSearch(search)
      setHasMoreData(true)
      startDateInputsChanged.current = false
    }

    let apiUrl =
      '/delivery-manager/requests/delivery?status=allocated,on_route,delivered,canceled&limit=20&had_problems_only=true'

    if (search) apiUrl += '&search=' + search

    if (minStartDate) apiUrl += `&start_date_begin=${minStartDate}`
    if (maxStartDate) apiUrl += `&start_date_end=${maxStartDate}`

    //offset nao pode ficar aqui
    const offset = requests && !isSearchDifferent ? requests.length : 0
    if (offset) apiUrl += '&offset=' + offset

    try {
      let { data } = await api.get(apiUrl)
      data = filterDeliveryRequestsByInternalTransfers(data)

      if (!data.length) {
        if (isSearchDifferent) setRequests(data)
        setHasMoreData(false)
      } else {
        if (!requests) setRequests(data)
        else setRequests(isSearchDifferent ? data : requests.concat(data))
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
  }, [search, lastSearch, requests, hasMoreData, maxStartDate, minStartDate, setIsLoading])

  //useEffect do loading inicial dos dados
  useEffect(() => {
    if (!requests && hasMoreData) loadData()
  }, [loadData, hasMoreData, requests])

  useEffect(() => {
    const handleScroll = () => {
      if (
        Math.abs(
          window.innerHeight +
            document.documentElement.scrollTop -
            document.documentElement.offsetHeight,
        ) > 10 ||
        isLoading
      ) {
        return
      }
      loadData()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isLoading, loadData])

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Entregas com problema">
      <LoadingSpinner text="Buscando entregas com problema. Aguarde..." />
      <Form style={{ color: '#A59E9E' }}>
        <Row
          className="d-flex justify-content-md-left justify-content-center"
          style={{ marginTop: '10px', marginInline: '15px' }}
        >
          <Col md={8} className="d-flex flex-column justify-content-center">
            <Row className="d-flex flex-column justify-content-center w-100">
              <SearchInput
                placeholder="Digite o código fiscal, placa do veículo, nome do motorista, nome do cliente ou nº do pedido/nota"
                searchValue={search}
                setValue={setSearch}
                handleSearch={loadData}
                showOptions={false}
                md={12}
              />
            </Row>
            <Row className="mt-2" style={{ paddingRight: '102px', marginRight: 0 }}>
              <Col md={6} style={{ paddingRight: '0px' }}>
                <div>
                  <Form.Label style={{ fontSize: 12, fontWeight: '500', marginBottom: 0 }}>
                    Data mínima
                  </Form.Label>
                  <Form.Control
                    style={{ fontSize: 14, maxWidth: 470 }}
                    type="date"
                    placeholder="Digite a data inicial"
                    max={formatDateTSX(maxStartDate, 'yyyy-MM-dd', false)}
                    value={formatDateTSX(minStartDate, 'yyyy-MM-dd', false)}
                    onChange={(e) => {
                      startDateInputsChanged.current = true
                      setMinStartDate(e.target.value)
                    }}
                  />
                </div>
              </Col>
              <Col md={6} style={{ paddingRight: '0px' }}>
                <div>
                  <Form.Label style={{ fontSize: 12, fontWeight: '500', marginBottom: 0 }}>
                    Data final
                  </Form.Label>
                  <Form.Control
                    style={{ fontSize: 14, maxWidth: 470 }}
                    type="date"
                    placeholder="Digite a data final"
                    min={formatDateTSX(minStartDate, 'yyyy-MM-dd', false)}
                    value={formatDateTSX(maxStartDate, 'yyyy-MM-dd', false)}
                    onChange={(e) => {
                      startDateInputsChanged.current = true
                      setMaxStartDate(e.target.value)
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Col>
          <Col
            md={4}
            className="d-flex flex-row justify-content-center align-items-start mt-md-0 mt-3"
          >
            <Button
              className={styles.newProblemBtn}
              onClick={() => navigate(`/gestor-entregas/entregas/problemas/novo`)}
            >
              <Plus
                style={{ marginBottom: 4, marginRight: 4 }}
                color="#fff"
                size={15}
                weight="bold"
              />
              Cadastrar problema
            </Button>
          </Col>
        </Row>
      </Form>
      {requestsChanged && (
        <Row>
          <Badge
            bg=""
            className={
              styles.infoBadge +
              ' d-flex flex-md-row flex-column justify-content-center align-items-center'
            }
          >
            <p className="my-0">A página foi atualizada com novas alterações. Confira-as abaixo.</p>
            <span className="m-2 my-0">
              <p className="m-0">
                <Button
                  color="white"
                  className={styles.closeNotificationButton + ' py-0 px-1 mt-md-0 mt-2'}
                  onClick={() => setRequestsChanged(false)}
                >
                  Fechar
                  <X size={15} weight="bold" style={{ paddingBottom: '2px' }} />
                </Button>
              </p>
            </span>
          </Badge>
        </Row>
      )}
      <Row className={`${styles.cardsContainer} justify-content-center justify-content-md-start`}>
        {isLoading && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 40,
            }}
          >
            <Spinner
              animation="border"
              style={{
                color: '#053C5E',
                marginRight: 10,
              }}
            />
          </div>
        )}
        {!requests?.length && !isLoading && (
          <Badge
            bg=""
            text="white"
            style={{ textAlign: 'center', marginTop: 40, backgroundColor: '#053c5e' }}
          >
            <Warning style={{ marginBottom: 2 }} color="#FFFFFF" size={15} weight="bold" /> Nenhuma
            entrega de pedido encontrada!
          </Badge>
        )}
        {requests
          ?.filter((r) => r.status === filteredStatus || !filteredStatus)
          .map((request) => (
            <Col
              key={request.id}
              className={styles.cardCol}
              style={{
                backgroundColor: request.delivery_problem_solution_note
                  ? 'rgba(0, 255, 0, 0.3)'
                  : '',
              }}
              md={5}
              lg={3}
            >
              <div>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip>{`Destinatário: ${request.receiver_name}`}</Tooltip>}
                >
                  <span className={styles.cardReceiverName}>{request.receiver_name}</span>
                </OverlayTrigger>
                {request.request_number && (
                  <span className={styles.cardRequestNumber}>Pedido {request.request_number}</span>
                )}

                <span className={styles.startDate}>
                  {`Entrega em: ${formatDateTSX(request.start_date, 'dd/MM/yyyy', false)}`}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Veículo:</strong>{' '}
                  {`${request.vehicle_model} (Placa ${request.vehicle_board})`}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Motorista:</strong> {request.driver_name}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Problema ocorrido:</strong> {request.request_delivery_driver_note}
                </span>
                <span className={styles.infoSpan}>
                  <strong>Solução:</strong>{' '}
                  {request.delivery_problem_solution_note || 'Não informada'}
                </span>
              </div>
              <div>
                <Button
                  className={styles.detailsButton}
                  onClick={() =>
                    navigate(`/gestor-entregas/entregas/problemas/${request.id}/editar`)
                  }
                >
                  Editar problema
                </Button>
                <Button
                  className={styles.detailsButton}
                  onClick={() => [setRequestModal(request), setShowRequestModal(true)]}
                >
                  Ver detalhes do pedido
                </Button>
              </div>
            </Col>
          ))}
      </Row>
      {requestModal && (
        <RequestModal
          request={requestModal}
          userRole="manager"
          showEditButton={true}
          showRequestModal={showRequestModal}
          setShowRequestModal={setShowRequestModal}
          onRequestChange={(updatedRequest: any) =>
            setRequestModal({
              ...updatedRequest,
              vehicle_model: requestModal.vehicle_model,
              created_at: requestModal.created_at,
            })
          }
          onRequestDeleted={(deletedRequestId) => {
            setRequests((prevRequests = []) =>
              prevRequests.filter((req) => req.id !== deletedRequestId),
            )
            setShowRequestModal(false)
          }}
        ></RequestModal>
      )}
      <Modal centered show={filterModal} onHide={() => setFilterModal(false)}>
        <Modal.Header closeButton />
        <Modal.Body>
          <span style={{ fontSize: 14, fontWeight: '500' }} className="text-left text-dark">
            Filtrar por status
          </span>
          <Row>
            <Col>
              <Form.Select
                className={styles.statusSelect}
                onChange={(event) => {
                  setFilteredStatus(event.target.value)
                  setFilterModal(false)
                }}
              >
                <option value="">Selecione uma opção</option>
                {Object.keys(deliveryStatusMap).map((key, index) => (
                  <option key={'map_' + index} value={key}>
                    {deliveryStatusMap[key]}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
          <Row className="text-center mt-2">
            <Col
              style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: '#053C5E',
              }}
            >
              <Button
                className={styles.clearFiltersBtn}
                onClick={() => {
                  setFilteredStatus('')
                  setFilterModal(false)
                }}
              >
                Limpar filtros
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </DashboardLayout>
  )
}

export default EntregasProblemas
